/**
 * useReviewExclusiveSubscription
 *
 * Owns the only business logic + state of the ReviewExclusiveSubscriptionSheet:
 * the in-flight flag and the dispatch to `ReviewExclusiveSubscriptionUseCase`.
 * Mirrors Flutter's
 * `lib/presentation/bottom_sheet/review_exclusive_subscription_bottom_sheet.dart`:
 *   - `:29` — `bool _isPerformingConfirmAction` is the sheet's entire relevant
 *     state; `isProcessing` here is its counterpart.
 *   - `:67-85` — `_reviewExclusiveSubscription(bool accept)` and its ordering:
 *     busy `true` (`:68-72`) -> `await` the review call (`:73-77`) -> busy
 *     `false` (`:78-82`) -> close the sheet (`:84`). This hook reproduces the
 *     first three steps; **the close is owned by the component** (see below).
 *   - Confirm (`:276`, `accept: true`) and Reject (`:297`, `accept: false`) both
 *     funnel through this single method, so the web exposes ONE parameterized
 *     `review(accept)` handler rather than two.
 *
 * Deliberately absent behaviours — Flutter's review flow has none of these, so
 * do NOT re-add them:
 *   1. No re-entrancy guard (contrast `subscribe_user_bottom_sheet.dart:225`) —
 *      the sheet's controls are disabled behind its loading overlay instead.
 *   2. No owner/blocked/suspended guard (contrast `:221-227` of that sibling) —
 *      authorization is the Cloud Function's, derived from `auth.uid`.
 *   3. No branching on the returned `bool` — Flutter discards it (`:73-77`), so
 *      there is no success toast, no error toast and no retry; the sheet closes
 *      identically on success and failure (`:84` is unconditional). A `false`
 *      most often means the request was already cancelled server-side
 *      (`cloud_functions/functions/src/functions/user_subscriptions/review_exclusive_subscription.ts:26-29`),
 *      for which an error toast would be actively WRONG copy. Do not copy
 *      `useSubscribeUser.ts:188-190`'s toast here.
 *   4. No local `actioned` write — Flutter never writes it; the CF sets it
 *      inside its transaction (`review_exclusive_subscription.ts:88-93`) and the
 *      inbox notification listener picks it up.
 *
 * This hook fires no sheet/modal/toast and performs no navigation — in
 * particular it does NOT call `SheetService.close()`. It exposes `isProcessing`
 * + `review`, and the owning `ReviewExclusiveSubscriptionSheetContent` wires the
 * close once `review` resolves (`presentation.md` §Page + feature-hook split;
 * `workflow/lessons.md` §Layer ownership: "Hooks never call `Navigator.*` and
 * never fire toasts/sheets/modals"). This is a DELIBERATE divergence from the
 * sibling `useSubscribeUser.ts`, which closes in-hook (`:129`, `:193`) and
 * predates that rule — the two siblings differ on purpose.
 */
import { useCallback, useMemo, useState } from 'react';
import type { ReviewExclusiveSubscriptionInput } from '@/domain/input/exclusiveContentSubscription/reviewExclusiveSubscriptionInput';
import { createReviewExclusiveSubscriptionUseCase } from '@/domain/usecases/exclusiveContentSubscription/reviewExclusiveSubscriptionUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseReviewExclusiveSubscriptionParams {
  /** Id of the `exclusiveSubscriptionRequested` notification being reviewed. */
  requestNotificationId: string;
  /** Id of the requesting (peer) user whose access is being reviewed. */
  userId: string;
}

export interface UseReviewExclusiveSubscriptionResult {
  /** Mirrors Flutter's `_isPerformingConfirmAction` (`:29`). */
  isProcessing: boolean;
  /** Confirm (`accept: true`) / Reject (`accept: false`) — Flutter `:67-85`. */
  review: (accept: boolean) => Promise<void>;
}

export const useReviewExclusiveSubscription = (
  params: UseReviewExclusiveSubscriptionParams,
): UseReviewExclusiveSubscriptionResult => {
  const { requestNotificationId, userId } = params;

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const review = useCallback(
    async (accept: boolean): Promise<void> => {
      // Flutter :68-72 — busy on before the await.
      setIsProcessing(true);

      const input: ReviewExclusiveSubscriptionInput = {
        requestNotificationId,
        userId,
        accept,
      };

      try {
        // Flutter :73-77 — the boolean result is intentionally discarded.
        await createReviewExclusiveSubscriptionUseCase().execute(input);
      } catch (error) {
        // Defence-in-depth only: the data layer logs and swallows to `false`,
        // so this path is not normally reachable. Produces no user-visible
        // output by design (see "deliberately absent" #3 above).
        appLogger.error(
          'reviewExclusiveSubscription threw',
          'useReviewExclusiveSubscription.review',
          { context: { requestNotificationId, userId, accept }, error: error as Error },
        );
      } finally {
        // Flutter :78-82 — busy off. In a `finally` so a throw cannot strand
        // the sheet behind its loading overlay.
        setIsProcessing(false);
      }
    },
    [requestNotificationId, userId],
  );

  return useMemo(
    () => ({ isProcessing, review }),
    [isProcessing, review],
  );
};
